import { useState } from "react";
import { Card } from "@/components/ui/card";
import PairingSequence from "@/components/PairingSequence";
import BreakdownLabel from "@/components/BreakdownLabel";
import { Route, ShieldCheck, AlertTriangle, ChevronDown } from "lucide-react";

export default function WinRoutes({ analysis, setup }) {
  const wr = analysis?.win_routes;
  const proj = analysis?.projection;
  const [open, setOpen] = useState(true);
  if (!wr || !wr.routes?.length) {
    return (
      <Card className="bg-[#121824]/70 border-slate-800 p-4" data-testid="win-routes-card">
        <h3 className="font-display font-bold uppercase tracking-wide text-sm flex items-center gap-2">
          <Route className="h-4 w-4 text-cyan-400" /> Paths to Victory
        </h3>
        <p className="text-xs text-amber-400/90 mt-2">No remaining pairing line reaches a {wr?.threshold || 3}-win majority from here.</p>
      </Card>
    );
  }

  const holdsVsPerfect = wr.routes.filter((r) => r.realism >= 0.999).length;

  return (
    <Card className="bg-[#121824]/70 border-cyan-700/30 p-4 sm:p-5" data-testid="win-routes-card">
      <button onClick={() => setOpen((v) => !v)} className="w-full flex items-center gap-2 text-left">
        <Route className="h-4 w-4 text-cyan-400 shrink-0" />
        <h3 className="font-display font-bold uppercase tracking-wide text-sm flex-1">
          Paths to Victory <span className="text-cyan-400">({wr.threshold}+ wins)</span>
        </h3>
        <span className="text-[11px] font-mono text-slate-400">{wr.count} route{wr.count === 1 ? "" : "s"}{wr.count > wr.routes.length ? ` · top ${wr.routes.length}` : ""}</span>
        <ChevronDown className={`h-4 w-4 text-slate-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      <p className="text-xs text-slate-500 mt-1">
        {proj && (
          holdsVsPerfect > 0
            ? <>Against a perfectly-playing opponent you project <b className="text-emerald-400">{proj.wins}–{proj.losses}</b>. Routes are sorted most-realistic first; lower ones need the opponent to misplay.</>
            : <>Against perfect play you project <b className={proj.wins >= wr.threshold ? "text-emerald-400" : "text-amber-400"}>{proj.wins}–{proj.losses}</b> — reaching {wr.threshold} wins needs the opponent to misstep. Most-realistic routes first.</>
        )}
      </p>

      {open && (
        <div className="mt-4 space-y-3 max-h-[620px] overflow-y-auto pr-1">
          {wr.routes.map((r, i) => {
            const perfect = r.realism >= 0.999;
            const errPct = Math.round((1 - r.realism) * 100);
            return (
              <div key={i} className="rounded-lg border border-slate-800 bg-slate-900/40 p-3" data-testid="win-route-row">
                <div className="flex items-center gap-2 flex-wrap mb-2">
                  <span className="text-[11px] font-mono text-slate-500">#{i + 1}</span>
                  <span className="font-mono text-base font-bold text-emerald-400" data-testid="route-record">{r.wins}–{r.losses}</span>
                  <BreakdownLabel breakdown={r.breakdown} className="text-[11px]" />
                  <div className="flex-1" />
                  {perfect ? (
                    <span className="text-[11px] flex items-center gap-1 text-emerald-300 bg-emerald-500/10 border border-emerald-600/30 rounded-full px-2 py-0.5" data-testid="route-realism">
                      <ShieldCheck className="h-3.5 w-3.5" /> Holds vs perfect play
                    </span>
                  ) : (
                    <span className="text-[11px] flex items-center gap-1 text-amber-300 bg-amber-500/10 border border-amber-600/30 rounded-full px-2 py-0.5" data-testid="route-realism">
                      <AlertTriangle className="h-3.5 w-3.5" /> Needs opponent to misstep ({errPct}%)
                    </span>
                  )}
                </div>
                <PairingSequence sequence={r.sequence} youAre={setup.you_are}
                  teamAName={setup.team_a_name} teamBName={setup.team_b_name} routeMode scale={0.8} />
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}
